import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SiteSettings {
  [key: string]: string;
}

interface SiteSettingsContextType {
  settings: SiteSettings;
  loading: boolean;
  getSetting: (key: string, fallback?: string) => string;
  updateSetting: (key: string, value: string, category?: string) => Promise<void>;
  updateSettings: (updates: { key: string; value: string; category?: string }[]) => Promise<void>;
  refreshSettings: () => Promise<void>;
}

const SiteSettingsContext = createContext<SiteSettingsContextType>({
  settings: {},
  loading: true,
  getSetting: () => "",
  updateSetting: async () => {},
  updateSettings: async () => {},
  refreshSettings: async () => {},
});

export const useSiteSettings = () => useContext(SiteSettingsContext);

export const SiteSettingsProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useState<SiteSettings>({});
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    const { data } = await supabase
      .from("site_settings" as any)
      .select("key, value");
    if (data) {
      const map: SiteSettings = {};
      (data as any[]).forEach((row: { key: string; value: string }) => {
        map[row.key] = row.value;
      });
      setSettings(map);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Apply CSS custom properties for colors + dark mode
  useEffect(() => {
    if (loading) return;
    const root = document.documentElement;

    // Dark mode toggle
    if (settings.dark_mode === "true") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    const colorKeys = [
      ["color_primary", "--primary"],
      ["color_secondary", "--secondary"],
      ["color_background", "--background"],
      ["color_foreground", "--foreground"],
      ["color_card", "--card"],
      ["color_muted", "--muted"],
      ["color_accent", "--accent"],
      ["color_border", "--border"],
    ];
    colorKeys.forEach(([settingKey, cssVar]) => {
      if (settings[settingKey]) {
        root.style.setProperty(cssVar, settings[settingKey]);
      }
    });

    // Border radius
    if (settings.border_radius) {
      root.style.setProperty("--radius", `${settings.border_radius}rem`);
    }

    // Global typography ---------------------------------------------------
    const headingFont = settings.typography_heading_font;
    const bodyFont = settings.typography_body_font;
    const buttonFont = settings.typography_button_font;
    const baseSize = settings.typography_base_size;
    const baseColor = settings.typography_base_color;
    const lineHeight = settings.typography_line_height;

    // Lazy-load Google Fonts when configured
    [headingFont, bodyFont, buttonFont].forEach((family) => {
      if (!family) return;
      const id = `design-font-${family.replace(/\s+/g, "-")}`;
      if (document.getElementById(id)) return;
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, "+")}:wght@400;500;600;700;800&display=swap`;
      document.head.appendChild(link);
    });

    // Inject / refresh the global typography stylesheet
    const STYLE_ID = "design-global-typography";
    let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    const rules: string[] = [];
    if (bodyFont) rules.push(`body, p, span, li, a, label, div { font-family: '${bodyFont}', sans-serif; }`);
    if (headingFont) rules.push(`h1, h2, h3, h4, h5, h6 { font-family: '${headingFont}', sans-serif !important; }`);
    if (buttonFont) rules.push(`button, .btn, [role="button"] { font-family: '${buttonFont}', sans-serif !important; }`);
    if (baseSize) rules.push(`html { font-size: ${baseSize}px; }`);
    if (baseColor) rules.push(`body { color: ${baseColor}; }`);
    if (lineHeight) rules.push(`body { line-height: ${lineHeight}; }`);
    styleEl.textContent = rules.join("\n");
  }, [settings, loading]);

  const getSetting = useCallback(
    (key: string, fallback = "") => settings[key] ?? fallback,
    [settings]
  );

  const updateSetting = useCallback(
    async (key: string, value: string, category?: string) => {
      const payload: any = { key, value, updated_at: new Date().toISOString() };
      if (category) payload.category = category;
      await supabase
        .from("site_settings" as any)
        .upsert(payload, { onConflict: "key" });
      setSettings((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const updateSettings = useCallback(
    async (updates: { key: string; value: string; category?: string }[]) => {
      const rows = updates.map((u) => ({
        key: u.key,
        value: u.value,
        category: u.category || "general",
        updated_at: new Date().toISOString(),
      }));
      await supabase
        .from("site_settings" as any)
        .upsert(rows as any, { onConflict: "key" });
      setSettings((prev) => {
        const next = { ...prev };
        updates.forEach((u) => {
          next[u.key] = u.value;
        });
        return next;
      });
    },
    []
  );

  return (
    <SiteSettingsContext.Provider
      value={{ settings, loading, getSetting, updateSetting, updateSettings, refreshSettings: fetchSettings }}
    >
      {children}
    </SiteSettingsContext.Provider>
  );
};
